import { getAlienPanicText } from './panicTable';

export const isAlienDiceResult = (result) =>
	Boolean(
		result &&
			(result.system === 'alien' ||
				result.alienResultType ||
				result.notation === 'Alien Roll' ||
				result.notation === 'Alien Panic')
	);

const getAlienSuccessLabel = (successes, t) =>
	t(successes === 1 ? 'diceResults.alien.successSingular' : 'diceResults.alien.successPlural');

const buildAlienSkillSummary = (result, t) => {
	const successes =
		result.successCount != null ? Number(result.successCount) : Number(result.value || 0);
	const panic = Boolean(result.panic);
	const successLabel = getAlienSuccessLabel(successes, t);
	const baseSummary = panic
		? t('diceResults.alien.summaryPanic', {
				count: successes,
				successLabel,
		  })
		: t('diceResults.alien.summary', {
				count: successes,
				successLabel,
		  });

	return result.isPushedRoll ? t('diceResults.alien.pushedPrefix', { summary: baseSummary }) : baseSummary;
};

const buildAlienSourceSummary = (result, t) => {
	if (result.sourceSuccessCount == null && !result.sourcePanic && !result.sourceWasPushed) {
		return '';
	}

	return buildAlienSkillSummary(
		{
			successCount: Number(result.sourceSuccessCount || 0),
			panic: Boolean(result.sourcePanic),
			isPushedRoll: Boolean(result.sourceWasPushed),
			value: Number(result.sourceSuccessCount || 0),
		},
		t
	);
};

const buildAlienPanicSummary = (result, t, locale) => {
	const activePanicResult =
		result.activePanicResult != null
			? Number(result.activePanicResult)
			: Number(result.value || 0);
	const panicEffectId = result.panicEffectId || result.panicEffect?.id;
	const panicText = getAlienPanicText(panicEffectId, locale);
	const title = panicText?.title || result.panicEffect?.title || '';
	const sourceSummary = buildAlienSourceSummary(result, t);
	const panicSummary = t('diceResults.alien.panicSummary', {
		result: activePanicResult,
		title,
	});

	return sourceSummary ? `${sourceSummary} -> ${panicSummary}` : panicSummary;
};

export const getAlienResultPresentation = (result, { t, locale, userName = '', statLabel = '' }) => {
	if (!isAlienDiceResult(result)) {
		return null;
	}

	if (result.alienResultType === 'panic' || result.panicRoll) {
		const summary = buildAlienPanicSummary(result, t, locale);
		const statInfo = statLabel ? ` (${statLabel})` : '';
		const message = t('diceResults.alien.panicResolved', {
			userName,
			statInfo,
			summary,
		});
		const panicSeverityValue = Number(result.rawPanicResult ?? result.activePanicResult ?? result.value ?? 0);
		const severity =
			panicSeverityValue >= 10 ? 'error' : panicSeverityValue >= 7 ? 'warning' : 'success';
		const panicEffectId = result.panicEffectId || result.panicEffect?.id;
		const panicText = getAlienPanicText(panicEffectId, locale);

		return {
			summary,
			message,
			severity,
			displayValue: summary,
			title: panicText?.description || result.description || summary,
		};
	}

	const summary = buildAlienSkillSummary(result, t);
	const statInfo = statLabel ? ` (${statLabel})` : '';
	const message = result.isPushedRoll
		? t('diceResults.alien.pushed', { userName, statInfo, summary })
		: t('diceResults.alien.rolled', { userName, statInfo, summary });
	const hasSuccess = Number(result.successCount ?? result.value ?? 0) > 0;
	const severity = hasSuccess ? (result.panic ? 'warning' : 'success') : 'error';

	return {
		summary,
		message,
		severity,
		displayValue: summary,
		title: summary,
	};
};
